import { useLocation } from "@reach/router"
import { useEffect, useRef } from "react"
import { toast } from "react-toastify"
import "react-toastify/dist/ReactToastify.minimal.css"

import { marketingClient } from "@trueskin-web/apis"
import { FieldCondition, Heading } from "@trueskin-web/components"
import { useAuth } from "@trueskin-web/context"
import { conditionalFunction } from "@trueskin-web/functions"
import { Trans, useTranslation } from "@trueskin-web/translations"

import ErrorToast from "./error-toast"
import BirthDate from "./form/birth-date"
import City from "./form/city"
import Country from "./form/country"
import Email from "./form/email"
import FirstName from "./form/first-name"
import LastName from "./form/last-name"
import PhoneNumber from "./form/phone-number"
import PostalCode from "./form/postal-code"
import Salution from "./form/salutation"
import ShippingAddressControl from "./form/shipping-address-control"
import StreetAndBuilding from "./form/street-and-building"
import TermsAgreement from "./form/terms-agreement"
import WizardForm from "./form/wizard-form"
import withPersonalDetails from "./with-personal-details"

const addressTypes = {
  BILLING: "billingAddress",
  SHIPPING: "shippingAddress",
}

const Shipping = ({
  userDetails,
  isDummyAccount,
  decorators,
  onSubmit,
  onCountryChange,
  onError,
  onPageLeave,
}) => {
  const { t } = useTranslation()

  const { hash } = useLocation()

  const {
    user: { id: userId },
  } = useAuth()

  const toastId = useRef(null)
  const shippingAddressRef = useRef(null)

  const { BILLING: billing, SHIPPING: shipping } = addressTypes

  useEffect(() => {
    conditionalFunction({
      condition: () => hash === "#shipping-address",
      success: scrollToTarget,
    })
  })

  useEffect(() => {
    marketingClient.sendEvent({
      name: marketingClient.EVENT_TYPES_ENUM.SHIPPING_PAGE_OPENED,
      data: {
        userId,
      },
    })

    return dismissErrorMessage
  }, [userId])

  const scrollToTarget = () => {
    const { current: targetEl = {} } = shippingAddressRef

    window.scrollTo({
      behavior: targetEl ? "smooth" : "auto",
      top: targetEl ? targetEl.offsetTop : 0,
    })
  }

  const notifyOnError = (...args) => {
    onError(...args)

    if (toast.isActive(toastId.current)) {
      return
    }

    toastId.current = toast.error(ErrorToast, {
      position: toast.POSITION.TOP_CENTER,
      autoClose: false,
      closeOnClick: true,
      draggablePercent: 60,
      toastId: "error-toast",
    })
  }

  const dismissErrorMessage = () => toast.dismiss(toastId.current)

  const onContinue = () => {
    dismissErrorMessage()

    if (!userDetails.birthDate) {
      marketingClient.trackEvent({
        name: marketingClient.EVENT_TYPES_ENUM.FIRST_SHIPPING_PAGE_FILLED,
      })
    }
  }

  return (
    <WizardForm
      initialValues={userDetails}
      decorators={decorators}
      saveFormDataOnPageExit
      onContinue={onContinue}
      onError={notifyOnError}
      onSubmit={onSubmit}
      onPageLeave={onPageLeave}
    >
      <WizardForm.Page key="contact" title={t("Start.Shipping.title")}>
        <Salution fieldName={`${billing}.salutation`} />
        <FirstName fieldName={`${billing}.firstName`} />
        <LastName fieldName={`${billing}.lastName`} />

        {isDummyAccount && <Email />}

        <PhoneNumber fieldName={`${billing}.phoneNumber`} />
        <BirthDate />
      </WizardForm.Page>

      <WizardForm.Page
        key="address"
        title={t("Start.Shipping.billingAddressTitle")}
      >
        <StreetAndBuilding fieldName={`${billing}.street`} type={billing} ha />
        <PostalCode
          fieldName={`${billing}.postalCode`}
          sx={{ gridColumn: [null, "span 1"] }}
        />
        <City
          fieldName={`${billing}.city`}
          sx={{ gridColumn: [null, "span 1"] }}
        />
        <Country
          onChange={onCountryChange}
          sx={{ gridColumn: [null, "span 1"] }}
        />
        <ShippingAddressControl scrollToTarget={scrollToTarget} />

        <FieldCondition when="isShippingAddressSameAsBillingAddress" is={false}>
          <Heading
            as="h3"
            size="xsm"
            sx={{ variant: "vwo.heading" }}
            ref={shippingAddressRef}
          >
            <Trans i18nKey="Start.Shipping.shippingAddressTitle" />
          </Heading>
          <Salution fieldName={`${shipping}.salutation`} />
          <FirstName fieldName={`${shipping}.firstName`} />
          <LastName fieldName={`${shipping}.lastName`} />
          <PhoneNumber fieldName={`${shipping}.phoneNumber`} />
          <StreetAndBuilding
            fieldName={`${shipping}.street`}
            type={billing}
          />{" "}
          <PostalCode
            fieldName={`${shipping}.postalCode`}
            sx={{ gridColumn: [null, "span 1"] }}
          />
          <City
            fieldName={`${shipping}.city`}
            sx={{ gridColumn: [null, "span 1"] }}
          />
          <Country
            disabled
            sx={{
              gridColumn: [null, "span 1"],
            }}
          />
        </FieldCondition>

        {isDummyAccount && <TermsAgreement />}
      </WizardForm.Page>
    </WizardForm>
  )
}

export default withPersonalDetails(Shipping)
